/**
 * RunPlanPanel — generates and displays an Executable Project Plan for an
 * EKSELENS360 assessment run, powered by Claude Opus 4.8.
 *
 * Renders inside the admin Assessment "view" page alongside ExportBar.
 *
 * UX:
 *   - First visit: shows a "Generate plan" button + optional constraints form.
 *   - After generation: shows engagement summary, the moves with their tasks /
 *     resources / risks / metrics / comms, the critical path, and the
 *     paste-into-a-board-pack executive narrative.
 *   - "Regenerate" button lets the consultant tweak constraints and re-run.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw, FileText, ChevronDown, ChevronUp, AlertTriangle, Target } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import InfoTooltip from "./InfoTooltip";
import GenerationProgress from "./GenerationProgress";

const fmtGbp = (n) => (typeof n === "number" ? "£" + n.toLocaleString("en-GB") : "—");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const IMPACT_COLOR = { high: "text-red-700 bg-red-50", med: "text-amber-700 bg-amber-50", low: "ek-text-soft ek-surface-2" };

function MoveCard({ move, index }) {
  const [open, setOpen] = useState(index < 2);
  return (
    <div className="border ek-line ek-surface" data-testid={`plan-move-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-3 flex items-start justify-between gap-3 hover:bg-[#FAF7FF]"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-semibold text-[#7737F6] tracking-wide">MOVE {index + 1}</span>
            <span className="text-[11px] ek-text-muted">· {move.duration_weeks ?? "?"}w · {fmtGbp(move.estimated_budget_gbp)}</span>
          </div>
          <div className="font-medium ek-text text-[14px]">{move.name}</div>
          <p className="text-[12.5px] ek-text-soft mt-1 line-clamp-2">{move.rationale}</p>
        </div>
        {open ? <ChevronUp size={16} className="ek-text-muted shrink-0 mt-1"/> : <ChevronDown size={16} className="ek-text-muted shrink-0 mt-1"/>}
      </button>
      {open && (
        <div className="border-t ek-line px-4 py-4 ek-surface-2 space-y-4 text-[13px]">
          {move.tasks?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] ek-text-muted mb-2">Tasks</div>
              <ol className="space-y-1.5">
                {move.tasks.map((t) => (
                  <li key={t.sequence} className="flex items-start gap-2">
                    <span className="ek-text-muted text-[11px] mt-0.5 w-5 text-right">{t.sequence}.</span>
                    <span className="flex-1">
                      <span className="font-medium ek-text">{t.title}</span>
                      <span className="ek-text-muted"> — {t.owner_role} · {t.effort_days}d</span>
                      {t.dependencies?.length > 0 && (
                        <span className="ek-text-muted text-[11px]"> (after {t.dependencies.join(",")})</span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {move.resource_model && Object.keys(move.resource_model).length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] ek-text-muted mb-2">Resource model (FTE-weeks)</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(move.resource_model).map(([role, fte]) => (
                  <span key={role} className="inline-flex items-center gap-1 ek-surface border ek-line px-2 py-1 text-[12px]">
                    <span className="ek-text">{role}</span>
                    <span className="text-[#7737F6] font-medium">{fte}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {move.milestones?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] ek-text-muted mb-2">Milestones</div>
              <ul className="space-y-1">
                {move.milestones.map((m, i) => (
                  <li key={`${m.week}-${m.milestone}`} className="ek-text-soft">
                    <span className="inline-block bg-[#F4EEFD] text-[#7737F6] text-[11px] font-semibold px-1.5 py-0.5 mr-2">W{m.week}</span>
                    {m.milestone}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {move.risks?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] ek-text-muted mb-2 flex items-center gap-1">
                <AlertTriangle size={11}/> Risks
              </div>
              <ul className="space-y-2">
                {move.risks.map((r, i) => (
                  <li key={`${r.risk}-${i}`} className="ek-surface border ek-line px-3 py-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-block text-[10px] font-semibold uppercase px-1.5 py-0.5 ${IMPACT_COLOR[r.likelihood] || ""}`}>L: {r.likelihood}</span>
                      <span className={`inline-block text-[10px] font-semibold uppercase px-1.5 py-0.5 ${IMPACT_COLOR[r.impact] || ""}`}>I: {r.impact}</span>
                    </div>
                    <div className="ek-text text-[12.5px]">{r.risk}</div>
                    <div className="ek-text-soft text-[12px] mt-1"><span className="ek-text-muted">Mitigation:</span> {r.mitigation}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {move.communications?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] ek-text-muted mb-2">Communications</div>
              <ul className="space-y-1.5">
                {move.communications.map((c, i) => (
                  <li key={`${c.when}-${c.channel}-${i}`} className="ek-text-soft">
                    <span className="ek-text-muted text-[11.5px]">{c.when} · {c.channel}:</span> <span className="ek-text">{c.audience}</span> — <span className="ek-text-soft">{c.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {move.success_metrics?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] ek-text-muted mb-2 flex items-center gap-1">
                <Target size={11}/> Success metrics
              </div>
              <ul className="space-y-1">
                {move.success_metrics.map((s, i) => (
                  <li key={`${s.metric}-${i}`} className="ek-text-soft">
                    <span className="ek-text">{s.metric}</span> → <span className="font-medium text-[#7737F6]">{s.target}</span> by <span className="ek-text-muted">{s.by_when}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function RunPlanPanel({ runId }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showConstraints, setShowConstraints] = useState(false);
  const [budget, setBudget] = useState("");
  const [months, setMonths] = useState("");
  const [fte, setFte] = useState("");
  const [appetite, setAppetite] = useState("moderate");
  // Polling state — when set, we keep GET-ing the plan every few seconds
  // until status flips to ready/failed (fire-and-poll pattern to dodge
  // Cloudflare's ~100s edge timeout on long Opus calls).
  const pollRef = useRef(null);
  const toastRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/plan`);
      setPlan(data.plan);
      // If we landed on a still-generating doc (page reload mid-job), resume polling
      if (data.plan?.status === "generating") setGenerating(true);
    } catch (e) {
      if (e?.response?.status !== 404) {
        toast.error(e?.response?.data?.detail || "Plan load failed");
      }
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); return () => stopPolling(); }, [load, stopPolling]);

  // When `generating` is true and no poller is running, start one
  useEffect(() => {
    if (!generating || pollRef.current) return;
    const startedAt = Date.now();
    const MAX_POLL_MS = 9 * 60 * 1000; // safety ceiling — backend marks jobs stale at 8 min
    const tick = async () => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        stopPolling(); setGenerating(false);
        toast.error("This is taking longer than expected. Refresh the page or regenerate to check.", { id: toastRef.current });
        toastRef.current = null;
        return;
      }
      try {
        const { data } = await api.get(`/admin/assessments/runs/${runId}/plan`);
        setPlan(data.plan);
        const st = data.plan?.status || "ready";
        if (st === "ready") {
          stopPolling(); setGenerating(false);
          toast.success(`Plan ready — ${data.plan?.payload?.moves?.length || 0} moves in ${((data.plan.elapsed_ms || 0)/1000).toFixed(1)}s`, { id: toastRef.current });
          toastRef.current = null;
        } else if (st === "failed") {
          stopPolling(); setGenerating(false);
          toast.error(data.plan?.error || "Plan generation failed", { id: toastRef.current });
          toastRef.current = null;
        }
      } catch (e) {
        // transient — keep polling
      }
    };
    pollRef.current = setInterval(tick, 4000);
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating, runId]);

  const generate = useCallback(async () => {
    const constraints = {};
    if (budget) constraints.budget_gbp = Number(budget);
    if (months) constraints.target_completion_months = Number(months);
    if (fte) constraints.fte_capacity = Number(fte);
    if (appetite) constraints.risk_appetite = appetite;

    if (generating) return;
    setGenerating(true);
    const ts = toast.loading("Queued — building the plan in the background (~1–3 min). You can switch tabs.");
    toastRef.current = ts;
    try {
      const { data } = await api.post(
        `/admin/assessments/runs/${runId}/plan`,
        { force_regenerate: !!plan && plan.status === "ready", constraints: Object.keys(constraints).length ? constraints : null },
      );
      setPlan(data.plan);
      if (data.cached) {
        setGenerating(false);
        toast.success("Loaded from cache", { id: ts });
      } else if (data.status === "ready") {
        setGenerating(false);
        toast.success(`Plan ready — ${data.plan?.payload?.moves?.length || 0} moves`, { id: ts });
      } else {
        toast.loading("Generating in the background — polling for completion…", { id: ts });
      }
      setShowConstraints(false);
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || e.message || "Plan generation failed", { id: ts });
    }
  }, [runId, plan, budget, months, fte, appetite, generating]);

  if (loading && !plan) return <div className="ek-surface border ek-line p-5 ek-text-muted text-[13px]">Loading plan…</div>;

  const pl = plan?.payload;
  return (
    <div className="ek-surface border ek-line p-5" data-testid="run-plan-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 ek-text-soft text-[12px] font-medium uppercase tracking-[0.16em]">
            <Sparkles size={14}/> Executable project plan
          </div>
          {plan && plan.status !== "generating" && (
            <div className="text-[11px] ek-text-muted mt-1">
              generated {fmtDate(plan.generated_at)}{plan.elapsed_ms ? ` · ${(plan.elapsed_ms / 1000).toFixed(1)}s` : ""}
              {plan.evidence_credibility_used && (
                <span className="ml-1 text-[#7737F6]">· defensibility-aware ({plan.evidence_credibility_used.overall_credibility_pct ?? "—"}%)</span>
              )}
              {plan.peer_benchmark_used && (
                <span className="ml-1 text-[#7737F6]">· peer context ({plan.peer_benchmark_used.confidence}, n={plan.peer_benchmark_used.sample_size})</span>
              )}
            </div>
          )}
          {plan && plan.status === "ready" && plan.quality && (
            <div className="mt-1.5" data-testid="plan-quality-badge">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] font-semibold tracking-[0.08em] uppercase ${
                  plan.quality.score >= 90 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                  plan.quality.score >= 70 ? "bg-amber-50 text-amber-700 border border-amber-200" :
                  "bg-red-50 text-red-700 border border-red-200"
                }`}
                title={plan.quality.violations?.length ? plan.quality.violations.join(" · ") : "Passes the Ekselens house-style checks"}
              >
                House-style {plan.quality.score}/100
                {plan.quality.passed ? " ✓" : ` · ${plan.quality.violations.length} flag${plan.quality.violations.length === 1 ? "" : "s"}`}
              </span>
              {!plan.quality.passed && (
                <ul className="mt-1 text-[10.5px] text-amber-700 list-disc list-inside" data-testid="plan-quality-flags">
                  {plan.quality.violations.slice(0, 5).map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              )}
            </div>
          )}
          {plan?.status === "generating" && (
            <div className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
              <RefreshCw size={11} className="animate-spin"/> Generating — runs in the background, ~1–3 min. You can switch tabs.
            </div>
          )}
          {plan?.status === "failed" && plan?.error && (
            <div className="text-[11px] text-red-700 mt-1">Generation failed — {plan.error}</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowConstraints((s) => !s)}
          className="text-[11px] text-[#7737F6] hover:text-[#5D28C7]"
          data-testid="plan-toggle-constraints"
        >
          {showConstraints ? "Hide constraints" : "Constraints…"}
        </button>
      </div>

      {showConstraints && (
        <div className="mb-3 grid grid-cols-2 gap-2 text-[12px]">
          <label>
            <span className="ek-text-muted text-[10.5px] uppercase tracking-wider block mb-0.5">Budget £<InfoTooltip testid="plan-budget-info">Hard cap on the total portfolio envelope. Opus will trim or de-scope moves to fit. Leave blank to let Opus size the work first, then negotiate.</InfoTooltip></span>
            <input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g. 450000"
              className="w-full border ek-line px-2 py-1 text-[13px]" data-testid="plan-budget"/>
          </label>
          <label>
            <span className="ek-text-muted text-[10.5px] uppercase tracking-wider block mb-0.5">Target months<InfoTooltip testid="plan-months-info">End-to-end duration target (months). Opus will sequence moves and surface the critical path. Below 6 months tends to force parallel streams; above 18 risks fatigue.</InfoTooltip></span>
            <input type="number" value={months} onChange={(e) => setMonths(e.target.value)} placeholder="e.g. 12"
              className="w-full border ek-line px-2 py-1 text-[13px]" data-testid="plan-months"/>
          </label>
          <label>
            <span className="ek-text-muted text-[10.5px] uppercase tracking-wider block mb-0.5">FTE capacity<InfoTooltip testid="plan-fte-info">Available client-side FTE the engagement can absorb concurrently. Constrains the resource model so the plan doesn&rsquo;t demand more bodies than the client has.</InfoTooltip></span>
            <input type="number" step="0.5" value={fte} onChange={(e) => setFte(e.target.value)} placeholder="e.g. 4.5"
              className="w-full border ek-line px-2 py-1 text-[13px]" data-testid="plan-fte"/>
          </label>
          <label>
            <span className="ek-text-muted text-[10.5px] uppercase tracking-wider block mb-0.5">Risk appetite<InfoTooltip testid="plan-appetite-info">Drives sequencing and pace. Conservative front-loads validation; aggressive accepts more parallel streams and a fatter risk register.</InfoTooltip></span>
            <select value={appetite} onChange={(e) => setAppetite(e.target.value)}
              className="w-full border ek-line px-2 py-1 text-[13px] ek-surface" data-testid="plan-appetite">
              <option value="conservative">conservative</option>
              <option value="moderate">moderate</option>
              <option value="aggressive">aggressive</option>
            </select>
          </label>
        </div>
      )}

      <GenerationProgress active={generating} label="Building the plan" typical="~2–3 min" testid="plan-generation-progress" />

      <button
        type="button"
        onClick={generate}
        disabled={generating}
        className="w-full bg-[#7737F6] hover:bg-[#5D28C7] disabled:opacity-60 text-white text-[13px] font-medium px-3 py-2 flex items-center justify-center gap-2 transition-colors"
        data-testid="plan-generate"
      >
        {generating ? <RefreshCw size={14} className="animate-spin"/> : <Sparkles size={14}/>}
        {generating ? "Generating…" : plan ? "Regenerate plan" : "Generate plan"}
      </button>

      <p className="text-[10.5px] ek-text-muted mt-2 leading-relaxed">
        Scoring is unchanged — this is engagement-grade narrative and delivery scaffolding only.
      </p>

      {pl && (
        <div className="mt-5 space-y-5">
          <div className="border-l-2 border-[#7737F6] pl-3 space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7737F6]">Headline finding</div>
            <p className="text-[13px] ek-text">{pl.engagement_summary?.headline_finding}</p>
            {pl.engagement_summary?.deal_breaker_implication && (
              <p className="text-[12.5px] text-amber-800 bg-amber-50 px-2 py-1 mt-1">
                <span className="font-semibold">Deal-breaker:</span> {pl.engagement_summary.deal_breaker_implication}
              </p>
            )}
            {pl.engagement_summary?.evidence_caveat && (
              <p className="text-[12.5px] text-orange-800 bg-orange-50 px-2 py-1 mt-1">
                <span className="font-semibold">Evidence caveat:</span> {pl.engagement_summary.evidence_caveat}
              </p>
            )}
            <div className="text-[11.5px] ek-text-muted pt-1">
              Owner: <span className="ek-text-soft font-medium">{pl.engagement_summary?.executive_owner_recommendation}</span>
            </div>
          </div>

          {pl.engagement_summary?.sector_lens && (
            <div className="text-[12.5px] ek-text-soft italic">{pl.engagement_summary.sector_lens}</div>
          )}

          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="ek-surface-2 border ek-line p-2">
              <div className="text-[10.5px] ek-text-muted uppercase tracking-wider">Duration</div>
              <div className="text-[18px] font-medium text-[#7737F6] mt-1">{pl.critical_path?.total_duration_weeks ?? "—"}<span className="text-[10px] ek-text-muted ml-1">weeks</span></div>
            </div>
            <div className="ek-surface-2 border ek-line p-2">
              <div className="text-[10.5px] ek-text-muted uppercase tracking-wider">Budget</div>
              <div className="text-[18px] font-medium text-[#7737F6] mt-1">{fmtGbp(pl.portfolio_view?.budget_total_gbp)}</div>
            </div>
            <div className="ek-surface-2 border ek-line p-2">
              <div className="text-[10.5px] ek-text-muted uppercase tracking-wider">Peak FTE</div>
              <div className="text-[18px] font-medium text-[#7737F6] mt-1">{pl.portfolio_view?.headcount_peak_fte ?? "—"}</div>
            </div>
          </div>

          {plan?.pricing && (
            <div className="border ek-line bg-[#FAF7FF] p-3" data-testid="plan-pricing-strip">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#7737F6]">Pricing applied</div>
                  <div className="text-[12.5px] ek-text mt-1">
                    Card: <span className="font-medium">{plan.pricing.rate_card_name}</span>
                    {plan.pricing.modifiers_applied?.region && <> · region <span className="font-medium">{plan.pricing.modifiers_applied.region}</span></>}
                    {plan.pricing.modifiers_applied?.sector && <> · sector <span className="font-medium">{plan.pricing.modifiers_applied.sector}</span></>}
                    {plan.pricing.modifiers_applied?.size && <> · size <span className="font-medium">{plan.pricing.modifiers_applied.size}</span></>}
                  </div>
                  <div className="text-[11px] ek-text-muted mt-1">
                    Cost confidence: <span className={`font-semibold ${
                      plan.pricing.cost_confidence === "high" ? "text-emerald-700" :
                      plan.pricing.cost_confidence === "medium" ? "text-amber-700" : "text-red-700"
                    }`}>{plan.pricing.cost_confidence}</span>
                    {plan.pricing.per_move?.some((m) => m.fallback_roles?.length > 0) && (
                      <span className="ml-2 text-amber-700">· some roles used the fallback rate — add them in the Rate cards tab to lift confidence</span>
                    )}
                  </div>
                </div>
                <a
                  href="/admin"
                  onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("admin-switch-tab", { detail: "Rate cards" })); }}
                  className="text-[11.5px] text-[#7737F6] hover:text-[#5D28C7] underline whitespace-nowrap"
                  data-testid="pricing-edit-card"
                >Edit rate card →</a>
              </div>
            </div>
          )}

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] ek-text-muted mb-2">Moves</div>
            <div className="space-y-2">
              {(pl.moves || []).map((m, i) => <MoveCard key={m.csi_move_id || i} move={m} index={i} />)}
            </div>
          </div>

          {pl.critical_path?.milestones?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] ek-text-muted mb-2">Critical path</div>
              <ul className="space-y-1 text-[13px]">
                {pl.critical_path.milestones.map((m, i) => (
                  <li key={`${m.week}-${m.milestone}-${i}`} className="ek-text-soft">
                    <span className="inline-block bg-[#F4EEFD] text-[#7737F6] text-[11px] font-semibold px-1.5 py-0.5 mr-2">W{m.week}</span>
                    {m.milestone}
                  </li>
                ))}
              </ul>
              {pl.critical_path.key_risks_to_path?.length > 0 && (
                <div className="mt-2 text-[12px] text-amber-800">
                  <span className="font-semibold">Key risks: </span>{pl.critical_path.key_risks_to_path.join("; ")}
                </div>
              )}
            </div>
          )}

          {pl.executive_narrative && (
            <div className="border ek-line ek-surface-2 p-4">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] ek-text-muted mb-2">
                <FileText size={11}/> Board-pack narrative
              </div>
              <p className="text-[13.5px] ek-text leading-relaxed">{pl.executive_narrative}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
